import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wind, Music, TrendingUp, Award, Sparkles, Home } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';
import { useAuth } from '@/contexts/AuthContext';

/**
 * JourneyOrientation - ÉCRAN FINAL "BOUSSOLE DU CALME"
 * Écran d'orientation post-Journey qui guide l'utilisateur vers sa première action
 * Une seule action principale recommandée, sans surcharge mentale
 */
const JourneyOrientation = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const { user } = useAuth();
  const [showContent, setShowContent] = useState(false);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';
  
  // Vérifier si l'utilisateur est Premium
  const isPremium = user?.isPremium || false;

  useEffect(() => {
    setIsFullscreen(true);
    
    // Fade-in progressif
    setTimeout(() => setShowContent(true), 300);
    
    return () => setIsFullscreen(false);
  }, [setIsFullscreen]);

  const handleStartTests = () => {
    // Navigation vers le test audiométrique (premier test)
    navigate('/audiometric-test');
  };

  const handleStartBreathing = () => {
    navigate('/breathing-premium');
  };

  const handleExploreTherapy = () => {
    // Navigation vers les paysages sonores
    navigate('/therapy');
  };

  const handleDiscoverPremium = () => {
    navigate('/subscribe');
  };

  const handleBackHome = () => {
    navigate('/home');
  };

  // Fonctionnalités accessibles depuis l'accueil
  const features = [
    {
      icon: Music,
      text: isEnglish ? 'Personalized Sound' : 'Son personnalisé',
    },
    {
      icon: Wind,
      text: isEnglish ? 'Guided Breathing' : 'Respiration guidée',
    },
    {
      icon: TrendingUp,
      text: isEnglish ? 'Track Your Progress' : 'Suivi de vos progrès',
    },
    {
      icon: Award,
      text: isEnglish 
        ? 'Badges & Consistency (unlocked after tests)' 
        : 'Badges & régularité (débloqués après les tests)',
    },
  ];

  return (
    <div 
      className="min-h-screen relative overflow-hidden flex flex-col items-center justify-start px-6 py-12"
      style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #a855f7 100%)',
      }}
    >
      {/* Background glow effect */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 30%, rgba(139, 92, 246, 0.4) 0%, transparent 60%)',
        }}
      />

      {/* Contenu principal - Fade-in */}
      <div
        className="relative z-10 w-full max-w-2xl mx-auto"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 600ms ease-out',
        }}
      >
        {/* 1. TITRE + SOUS-TEXTE */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4 tracking-wide">
            {isEnglish 
              ? 'Your path to calm starts here'
              : 'Votre chemin vers le calme commence ici'
            }
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mb-2">
            {isEnglish
              ? 'You can now access all Hearen experiences, at your own pace.'
              : 'Vous pouvez maintenant retrouver toutes les expériences Hearen, à votre rythme.'
            }
          </p>
          <p className="text-white/80 text-base">
            {isEnglish
              ? 'To start simply, Hearen recommends:'
              : 'Pour commencer simplement, Hearen vous recommande :'
            }
          </p>
        </div>

        {/* 2. CARTE PRINCIPALE - ACTION RECOMMANDÉE : TESTS AUDITIFS (CTA PRIORITAIRE) */}
        <div
          className="mb-10"
          style={{
            opacity: showContent ? 1 : 0,
            transform: showContent ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 600ms ease-out 120ms',
          }}
        >
          <div
            className="bg-white/15 backdrop-blur-lg border border-white/20 rounded-3xl p-8 text-center relative overflow-hidden"
            style={{
              boxShadow: '0 20px 60px rgba(59, 130, 246, 0.5), 0 0 40px rgba(59, 130, 246, 0.3)',
            }}
          >
            {/* Icône tests - ondes sonores */}
            <div className="flex justify-center mb-6">
              <div
                className="relative w-28 h-28 rounded-full flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, rgba(37, 99, 235, 0.3) 70%)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(59, 130, 246, 0.6)',
                  boxShadow: '0 8px 30px rgba(59, 130, 246, 0.5)',
                  animation: 'orb-gentle-breathe 4s ease-in-out infinite',
                }}
              >
                {/* Icône d'oreille/test */}
                <div className="text-5xl">🎧</div>
              </div>
            </div>

            {/* Titre */}
            <h2 className="text-3xl font-bold text-white mb-3">
              {isEnglish ? 'Start with your hearing tests' : 'Commencer les tests auditifs'}
            </h2>

            {/* Texte explicatif pédagogique */}
            <p className="text-white/95 text-base mb-6 leading-relaxed px-4">
              {isEnglish
                ? 'Hearing tests help Hearen adapt the listening experience to your profile.'
                : 'Les tests auditifs permettent à Hearen d\'adapter précisément les paysages sonores à votre profil.'
              }
            </p>

            {/* CTA Principal - Tests */}
            <button
              onClick={handleStartTests}
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-blue-900"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #dbeafe 100%)',
                boxShadow: '0 8px 30px rgba(255, 255, 255, 0.4)',
              }}
            >
              {isEnglish ? 'Start the tests' : 'Commencer les tests'}
            </button>
          </div>
        </div>

        {/* 2B. ACTIONS SECONDAIRES - Respiration et Thérapie */}
        <div
          className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4"
          style={{
            opacity: showContent ? 1 : 0,
            transform: showContent ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 600ms ease-out 180ms',
          }}
        >
          {/* Action secondaire : Respiration */}
          <div
            className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-6 text-center"
            style={{
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
            }}
          >
            <div className="flex justify-center mb-3">
              <Wind className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {isEnglish ? 'Breathing' : 'Respiration'}
            </h3>
            <p className="text-white/70 text-sm mb-4">
              {isEnglish ? 'Guided breathing exercises' : 'Exercices de respiration guidée'}
            </p>
            <button
              onClick={handleStartBreathing}
              className="w-full px-6 py-3 rounded-full font-medium text-sm transition-all hover:scale-105 active:scale-95 text-white"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              {isEnglish ? 'Explore' : 'Explorer'}
            </button>
          </div>

          {/* Action secondaire : Thérapie Sonore */}
          <div
            className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-6 text-center"
            style={{
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
            }}
          >
            <div className="flex justify-center mb-3">
              <Music className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {isEnglish ? 'Sound' : 'Son'}
            </h3>
            <p className="text-white/70 text-sm mb-4">
              {isEnglish ? 'Personalized soundscapes' : 'Paysages sonores personnalisés'}
            </p>
            <button
              onClick={handleExploreTherapy}
              className="w-full px-6 py-3 rounded-full font-medium text-sm transition-all hover:scale-105 active:scale-95 text-white"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              {isEnglish ? 'Explore' : 'Explorer'}
            </button>
          </div>
        </div>

        {/* 3. CARTE SECONDAIRE - RETROUVER LES FONCTIONNALITÉS */}
        <div
          className="mb-8"
          style={{
            opacity: showContent ? 1 : 0,
            transform: showContent ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 600ms ease-out 240ms',
          }}
        >
          <div
            className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-3xl p-6"
            style={{
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}
          >
            {/* Titre */}
            <h3 className="text-white font-semibold text-xl mb-5 text-center">
              {isEnglish ? 'Explore next' : 'Explorer ensuite'}
            </h3>

            {/* Liste des fonctionnalités */}
            <div className="space-y-3 mb-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <feature.icon className="w-5 h-5 text-white/80 flex-shrink-0" />
                  <span className="text-white/90 text-sm">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Texte rassurant */}
            <p className="text-white/70 text-sm text-center leading-relaxed">
              {isEnglish
                ? 'All these spaces are accessible from the home screen, whenever you wish.'
                : "Tous ces espaces sont accessibles depuis l'écran d'accueil, quand vous le souhaitez."
              }
            </p>
          </div>
        </div>

        {/* 4. SECTION PREMIUM (OPTIONNELLE ET DOUCE) - VISIBLE UNIQUEMENT POUR FREE */}
        {!isPremium && (
          <div
            className="mb-8"
            style={{
              opacity: showContent ? 1 : 0,
              transform: showContent ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 600ms ease-out 360ms',
            }}
          >
            <div
              className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-purple-400/30 rounded-3xl p-6 text-center"
              style={{
                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.25)',
              }}
            >
              {/* Icône Premium */}
              <div className="flex justify-center mb-3">
                <Sparkles className="w-7 h-7 text-yellow-300" />
              </div>

              {/* Texte Premium */}
              <p className="text-white/90 text-sm leading-relaxed mb-4">
                {isEnglish
                  ? 'Unlock the Hera voice, complete sound therapy, and personalized programs.'
                  : 'Débloquez la voix Hera, les paysages sonores complète et les programmes personnalisés.'
                }
              </p>

              {/* Bouton Premium (secondaire) */}
              <button
                onClick={handleDiscoverPremium}
                className="px-6 py-3 rounded-full font-medium text-sm transition-all hover:scale-105 active:scale-95 text-white"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                {isEnglish ? 'Discover Premium' : 'Découvrir le Premium'}
              </button>
            </div>
          </div>
        )}

        {/* 5. SORTIE DU JOURNEY - Bouton discret */}
        <div
          className="text-center"
          style={{
            opacity: showContent ? 1 : 0,
            transition: 'opacity 600ms ease-out 480ms',
          }}
        >
          <button
            onClick={handleBackHome}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all hover:scale-105 active:scale-95 text-white/80 hover:text-white"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Home className="w-4 h-4" />
            {isEnglish ? 'Back to home' : "Retour à l'accueil"}
          </button>
        </div>
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes orb-gentle-breathe {
          0%, 100% { 
            transform: scale(1);
            opacity: 0.9;
          }
          50% { 
            transform: scale(1.08);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default JourneyOrientation;
